import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Clock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIdleTimer } from "@/hooks/use-idle-timer";
import { logoutWithServer } from "@/stores/auth.store";
import { useUIStore } from "@/stores/ui.store";

export function IdleStatus() {
  const navigate = useNavigate();
  const session = useUIStore((s) => s.session);

  const handleTimeout = useCallback(() => {
    void logoutWithServer();
    navigate("/login", { replace: true });
  }, [navigate]);

  const { state, countdown, reset } = useIdleTimer({
    badgeIdleMs: session.idleBadgeSeconds * 1000,
    idleMs: session.logoutMinutes * 60 * 1000,
    warningMs: session.warningSeconds * 1000,
    onTimeout: handleTimeout,
  });

  const isWarning = state === "warning";
  const isIdleOrWarning = state === "idle" || isWarning;

  return (
    <>
      {/* Status badge — hidden if user disabled it */}
      {session.showIdleBadge && (
        <div
          className={cn(
            "hidden sm:flex items-center gap-1 rounded-full transition-colors select-none",
            session.badgeStyle === "icon-only"
              ? "p-1"
              : cn(
                  "border px-2 py-0.5 text-[11px] font-medium",
                  isIdleOrWarning
                    ? "border-amber-400/60"
                    : "border-emerald-400/40"
                ),
            isIdleOrWarning
              ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
              : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          )}
          title={isIdleOrWarning ? "Session is idle" : "Session active"}
        >
          {isIdleOrWarning ? (
            <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
          ) : (
            <Activity className="h-3 w-3 shrink-0" aria-hidden="true" />
          )}
          {session.badgeStyle === "icon-text" && (
            <span>{isIdleOrWarning ? "Idle" : "Active"}</span>
          )}
        </div>
      )}

      {/* Countdown warning dialog */}
      <Dialog open={isWarning} onOpenChange={(open) => { if (!open) reset(); }}>
        <DialogContent className="max-w-sm" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500 shrink-0" />
              Session expiring soon
            </DialogTitle>
            <DialogDescription>
              You've been inactive. Your session will automatically log out in{" "}
              <span className="font-bold tabular-nums text-foreground">
                {countdown}s
              </span>
              .
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={handleTimeout}>
              Log out now
            </Button>
            <Button onClick={reset}>
              Stay signed in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
